import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { capitalizeFirstLetter } from "../../utils/letters";
import { getCourseMessages, sendCourseMessage } from "../../api/academic";

function formatMessageDate(iso) {
	if (!iso) return "";
	const d = new Date(iso);
	return d.toLocaleDateString("es-CL", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export default function CourseMessagesBlock({ runId, recipientId = null, title = "Mensajes del curso", variant = "card", onSendOrLoad, isDrawerOpen }) {
	const isDrawer = variant === "drawer";
	const { user } = useAuth();
	const currentUserId = user?.id;
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(!!runId);
	const [error, setError] = useState(null);
	const [body, setBody] = useState("");
	const [sending, setSending] = useState(false);
	const [messageEmptyError, setMessageEmptyError] = useState(false);
	
	const textareaRef = useRef(null);
	const messagesEndRef = useRef(null);

	const isNoTeacherError = error && (
		typeof error === "string" && error.toLowerCase().includes("no tiene docente asignado")
	);

	const load = useCallback(async (silent = false) => {
		if (!runId) return;
		if (!silent) {
			setLoading(true);
			setError(null);
		}
		try {
			const data = await getCourseMessages(runId, recipientId ?? undefined);
			const list = data.messages != null ? data.messages : [];
			setMessages(Array.isArray(list) ? list : []);
			if (onSendOrLoad) onSendOrLoad();
		} catch (e) {
			if (!silent) setError(e?.response?.data?.error || "No se pudieron cargar los mensajes.");
		} finally {
			if (!silent) setLoading(false);
		}
	}, [runId, recipientId, onSendOrLoad]);

	useEffect(() => {
		load(false);
	}, [load]);

	useEffect(() => {
		if (!isDrawer || !isDrawerOpen || !runId) return;
		const interval = setInterval(() => load(true), 3000);
		return () => clearInterval(interval);
	}, [isDrawer, isDrawerOpen, runId, load]);

	const handleSend = async (e) => {
		e.preventDefault();
		const text = (body || "").trim();
		if (sending) return;
		if (!text) {
			setMessageEmptyError(true);
			return;
		}
		setMessageEmptyError(false);
		setSending(true);
		setError(null);
		try {
			const newMsg = await sendCourseMessage(runId, text, recipientId ?? undefined);
			setMessages((prev) => [...prev, newMsg]);
			setBody("");
			if (onSendOrLoad) onSendOrLoad();
		} catch (e) {
			setError(e?.response?.data?.error || e?.response?.data?.body?.[0] || "Error al enviar.");
		} finally {
			setSending(false);
			setTimeout(() => textareaRef.current?.focus(), 0);
		}
	};

	// EFECTO DE SCROLL INSTANTÁNEO ("Pum" directo al final)
	useEffect(() => {
		// Al usar "auto" en lugar de "smooth", el salto es inmediato sin animación
        messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    }, [messages, isDrawerOpen]);

	if (!runId) return null;

	const wrapClass = isDrawer ? "course-messages-block course-messages-block--drawer" : "aula-card course-messages-block";
	
	return (
		<div className={wrapClass}>
			
			{title ? <h3 className="aula-card-title">{title}</h3> : null}
			{error && (
				<div className="alert alert-danger py-2 small mb-2" role="alert">
					{isNoTeacherError
						? "Para enviar mensajes, este curso debe tener un docente asignado. Contacta al administrador."
						: error}
				</div>
			)}
			{loading ? (
				<p className="text-muted small">Cargando mensajes...</p>
			) : (
				<>
					<div className="course-messages-list" aria-label="Lista de mensajes">
						{messages.length === 0 ? (
							<p className="text-muted small mb-2">Aún no hay mensajes. Escribe uno abajo.</p>
						) : (
							<ul className="course-messages-ul">
								{messages.map((msg) => {
									const isOwn = currentUserId && msg.sender?.id === currentUserId;
									const name = isOwn
										? "Tú"
										: [msg.sender?.first_name, msg.sender?.last_name].filter(Boolean).join(" ") || "Usuario";
									const isTeacher = msg.sender?.is_teacher;
									return (
										<li key={msg.id} className={`course-message-item ${isOwn ? "own" : ""}`}>
											<div className="course-message-meta">
												<span className="course-message-sender">
													{capitalizeFirstLetter(name)}
													{isTeacher && <span className="course-message-badge">· Docente</span>}
												</span>
												<time className="course-message-time" dateTime={msg.created_at}>
													{formatMessageDate(msg.created_at)}
												</time>
											</div>
											<p>{msg.body}</p>
										</li>
									);
								})}
								{/* EL ANCLA INVISIBLE AL FINAL DE LA LISTA */}
								<div ref={messagesEndRef} style={{ float:"left", clear: "both" }} />
							</ul>
						)}
					</div>
					<form className="course-messages-form" onSubmit={handleSend}>
						{messageEmptyError && (
							<p className="invalid-feedback d-block mb-1" role="alert">Escribe un mensaje antes de enviar.</p>
						)}
						<textarea
							ref={textareaRef}
							className={`form-control course-messages-input ${messageEmptyError ? "is-invalid" : ""}`}
							aria-invalid={!!messageEmptyError}
							placeholder={isNoTeacherError ? "No disponible: el curso no tiene docente asignado." : "Escribe un mensaje al docente... (Enter para enviar, Shift+Enter nueva línea)"}
							value={body}
							onChange={(e) => { setBody(e.target.value); if (messageEmptyError) setMessageEmptyError(false); }}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									if ((body || "").trim() && !sending && !isNoTeacherError) handleSend(e);
								}
							}}
							rows={2}
							maxLength={5000}
							disabled={sending || isNoTeacherError}
							aria-label="Texto del mensaje"
						/>
						<button type="submit" className="admin-btn" disabled={sending || !body.trim() || isNoTeacherError}>
							<span>{sending ? "Enviando..." : "Enviar"}</span>
						</button>
					</form>
				</>
			)}
		</div>
	);
}